import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import {
    GetDeliveryPickupPointsMethod,
    type GetDeliveryPickupPointsPathParameters,
    type GetDeliveryPickupPointsSearchParams,
    getGetDeliveryPickupPointsUrl,
} from '@/shared/api/openApi';
import { OrderDeliveryServices } from '@/shared/api/order';
import type { PickupPoint, SelfPickupPointRestriction } from '@/shared/api/pickupPoint';
import { request } from '@/shared/api/request';
import type { PickupPointDto } from './types/PickupPointDto';
import type { PickupPointRequestParams } from './types/PickupPointRequestParams';
import { type SelfPickupPointDto } from './types/SelfPickupPointDto';
import { type SelfPickupPointRequestParams } from './types/SelfPickupPointRequestParams';
import { type SelfPickupPointRequestResponse } from './types/SelfPickupPointRequestResponse';

const toPickupPointModel = (point: PickupPointDto): PickupPoint => {
    return {
        id: point.id,
        name: point.name,
        comment: point.address.comment,
        schedule: point.schedule.restrictions,
        address: point.address.full_address,
        paymentMethods: point.payment_methods,
        position: point.position,
        serviceType: point.service_type,
    };
};

const fromSelfPickupPoint = (point: SelfPickupPointDto): PickupPoint => {
    return {
        id: point.warehouse_id,
        name: point.title,
        comment: '',
        schedule: (point.schedule.restrictions || []).map((restriction: SelfPickupPointRestriction) => {
            return {
                days: [restriction.day],
                time_from: restriction.time_from as string,
                time_to: restriction.time_to as string,
            };
        }),
        address: point.full_address,
        isAvailable: point.is_available,
        paymentMethods: point.payment_methods,
        position: {
            latitude: point.latitude,
            longitude: point.longitude,
        },
        serviceType: OrderDeliveryServices.SELF,
        itemsCount: point.items_count,
        deliveryInterval: point.delivery_interval,
    };
};

const api = createApi({
    name: 'entities/pickupPoint/api',
    baseQuery: createBaseQuery(request),
    endpoints: (builder) => {
        return {
            PickupPoints: builder.query<
                PickupPoint[],
                GetDeliveryPickupPointsSearchParams & GetDeliveryPickupPointsPathParameters
            >({
                query: ({ store_slug, ...payload }) => {
                    return {
                        url: getGetDeliveryPickupPointsUrl({ store_slug }),
                        method: GetDeliveryPickupPointsMethod,
                        params: payload,
                    };
                },
                // Не нужно держать в кэше все ответы по этой ручке
                serializeQueryArgs: () => 'PickupPoints/PickupPoints',
                keepUnusedDataFor: 0,
                transformResponse: (response: PickupPointDto[]): PickupPoint[] => response.map(toPickupPointModel),
            }),
            PickupPoint: builder.query<PickupPoint | null, PickupPointRequestParams>({
                query: (payload) => {
                    return {
                        url: `/${payload.storeSlug}/delivery/points`,
                        method: 'GET',
                        params: {
                            pickup_point_id: payload.targetId,
                            service_type: payload.serviceType,
                            type: 'pickup_point',
                            order_id: payload.orderId,
                        },
                    };
                },
                transformResponse: ([point]: PickupPointDto[]): PickupPoint | null => {
                    if (!point) {
                        return null;
                    }

                    return toPickupPointModel(point);
                },
            }),

            SelfPickupPoints: builder.query<
                SelfPickupPointRequestResponse,
                GetDeliveryPickupPointsSearchParams &
                    GetDeliveryPickupPointsPathParameters & { multiWarehousesEnabled?: boolean }
            >({
                query: (payload) => {
                    return {
                        url: `/${payload.store_slug}/orders/${payload.order_id}/delivery/selfpickupoptions`,
                        method: 'GET',
                        params: payload,
                    };
                },
                // Не нужно держать в кэше все ответы по этой ручке
                serializeQueryArgs: () => 'SelfPickupPoints/SelfPickupPoints',
                keepUnusedDataFor: 0,
                transformResponse: (
                    response: {
                        options: SelfPickupPointDto[];
                        total_items_count: number;
                    },
                    _meta,
                    { multiWarehousesEnabled }
                ): SelfPickupPointRequestResponse => {
                    return {
                        itemsCount: response.total_items_count,
                        //  TODO SS-8031 После полноценного перехода на мультизаказ убрать фильтрацию по point.isAvailable и отвязаться от multiWarehousesEnabled
                        points: response.options.map(fromSelfPickupPoint).filter((point) => {
                            if (multiWarehousesEnabled) {
                                return Boolean(point.itemsCount && point.itemsCount > 0);
                            }

                            return Boolean(point.isAvailable);
                        }),
                    };
                },
            }),

            SelfPickupPoint: builder.query<PickupPoint | null, SelfPickupPointRequestParams>({
                query: (payload) => {
                    return {
                        url: `/${payload.storeSlug}/orders/${payload.orderId}/delivery/selfpickupoptions`,
                        method: 'GET',
                        params: {
                            self_pick_up_warehouse_id: payload.targetId,
                        },
                    };
                },
                transformResponse: (response: { options: SelfPickupPointDto[] }): PickupPoint | null => {
                    const point = response.options[0];

                    if (!point) {
                        return null;
                    }

                    return fromSelfPickupPoint(point);
                },
            }),
        };
    },
});

export const useLazyPickupPoints = api.useLazyPickupPointsQuery;

export const useLazySelfPickupPoints = api.useLazySelfPickupPointsQuery;

export const getPickupPoints = api.endpoints.PickupPoints.query;

export const getPickupPoint = api.endpoints.PickupPoint.query;

export const getSelfPickupPoint = api.endpoints.SelfPickupPoint.query;
